// Pure resolver for the recovery continuation. Recognizes the three callback
// shapes the registered platform receivers handle (host `auth`, path
// /reset-password) and fails closed on everything else rather than
// interchanging credentials into a guessed native call.
// Never authenticates, verifies, consumes, stores, logs, or forwards tokens.

import { Platform } from './platform';

export const NATIVE_RECOVERY_SCHEME = 'supabase.app.ridepulse.ios';
export const ANDROID_RECOVERY_SCHEME = 'supabase.app.ridepulse.android';
// Fixed per-platform receivers declared by each native manifest. 'other'
// (desktop/unknown) stays on the iOS choice with device guidance — the URL
// is never taken from or influenced by callback parameters.
const NATIVE_RECOVERY_SCHEMES: Record<Platform, string> = {
  ios: NATIVE_RECOVERY_SCHEME,
  android: ANDROID_RECOVERY_SCHEME,
  other: NATIVE_RECOVERY_SCHEME,
};
export const WEB_RECOVERY_PATH = '/auth/reset-password';
const MAX_TOKEN_CHARS = 4096;
const MAX_JWT_CHARS = 16384;

const CREDENTIAL_KEYS = new Set([
  'token',
  'code',
  'token_hash',
  'access_token',
  'refresh_token',
]);
const ERROR_KEYS = new Set(['error', 'error_code', 'error_description']);

export type RecoveryCallbackKind = 'tokenHash' | 'pkce' | 'implicit';

export type NativeRecoveryStatus = 'ready' | 'missing' | 'unsupported' | 'invalid';

export type NativeRecoveryContinuation =
  | { status: 'ready'; kind: RecoveryCallbackKind; url: string }
  | { status: Exclude<NativeRecoveryStatus, 'ready'> };

export interface RecoveryLocationLike {
  pathname: string;
  search: string;
  hash: string;
}

export interface RecoveryCredentials {
  token?: string;
  code?: string;
  accessToken?: string;
  refreshToken?: string;
}

export type ParsedRecoveryCallback =
  | { status: 'ready'; kind: RecoveryCallbackKind; typePresent: boolean; credentials: RecoveryCredentials }
  | { status: Exclude<NativeRecoveryStatus, 'ready'> };

type Status = Exclude<NativeRecoveryStatus, 'ready'>;
const fail = (status: Status): { status: Status } => ({ status });

const parsePairs = (raw: string): Array<[string, string]> | null => {
  const pairs: Array<[string, string]> = [];
  if (!raw) return pairs;
  for (const part of raw.split('&')) {
    if (!part) continue;
    const eq = part.indexOf('=');
    const rawKey = eq === -1 ? part : part.slice(0, eq);
    const rawValue = eq === -1 ? '' : part.slice(eq + 1);
    try {
      pairs.push([decodeURIComponent(rawKey), decodeURIComponent(rawValue)]);
    } catch {
      return null;
    }
  }
  return pairs;
};

// Credentials arrive pre-encoded by the provider; a raw '%' or '+' is an
// encoding artifact we will not guess at, and decoded whitespace/control
// characters can never be a valid token.
const cleanCredential = (raw: string, maxChars: number): string | null => {
  if (!raw || raw.length > maxChars) return null;
  if (raw.includes('%') || raw.includes('+')) return null;
  if (/\s/.test(raw)) return null;
  for (const c of raw) {
    const cp = c.codePointAt(0) ?? 0;
    if (cp < 0x20 || cp === 0x7f) return null;
  }
  return raw;
};

// Parsed callback with private credentials for the admin adapter. Non-ready
// results carry status only — never credential values.
export function parseRecoveryCallback(
  location: RecoveryLocationLike
): ParsedRecoveryCallback {
  if (location.pathname !== WEB_RECOVERY_PATH) return fail('unsupported');

  const query = parsePairs(location.search.startsWith('?') ? location.search.slice(1) : location.search);
  const fragment = parsePairs(location.hash.startsWith('#') ? location.hash.slice(1) : location.hash);
  if (!query || !fragment) return fail('invalid');

  const all = [...query, ...fragment];
  if (all.some(([k]) => ERROR_KEYS.has(k))) return fail('invalid');

  for (const key of Array.from(CREDENTIAL_KEYS)) {
    if (all.filter(([k]) => k === key).length > 1) return fail('invalid');
  }
  const types = all.filter(([k]) => k === 'type').map(([, v]) => v);
  if (types.length > 1) return fail('invalid');
  if (types.length === 1 && types[0] !== 'recovery') return fail('invalid');

  const qVal = (k: string) => query.filter(([key]) => key === k).map(([, v]) => v)[0];
  const fVal = (k: string) => fragment.filter(([key]) => key === k).map(([, v]) => v)[0];
  const qCreds = query.filter(([k]) => CREDENTIAL_KEYS.has(k)).length;
  const fCreds = fragment.filter(([k]) => CREDENTIAL_KEYS.has(k)).length;
  const qType = query.some(([k]) => k === 'type');
  const fType = fragment.some(([k]) => k === 'type');

  if (all.filter(([k]) => CREDENTIAL_KEYS.has(k)).length === 0 && types.length === 0) {
    return fail('missing');
  }
  // Credentials must not mix query and fragment transports.
  if (qCreds > 0 && fCreds > 0) return fail('invalid');
  // token_hash is a recognized but unsupported shape for this contract.
  if (all.some(([k]) => k === 'token_hash')) return fail('unsupported');

  // Credential keys in the wrong transport (token/code in fragment,
  // access/refresh in query) are recognized but not a valid contract shape.
  if (fragment.some(([k]) => ['token', 'code', 'token_hash'].includes(k))) {
    return fail('unsupported');
  }
  if (query.some(([k]) => ['access_token', 'refresh_token', 'token_hash'].includes(k))) {
    return fail('unsupported');
  }

  if (fCreds > 0 || (fragment.length > 0 && fType)) {
    // Implicit: exactly access+refresh+recovery type in the fragment; the
    // query carries no credential or type influence. Provider tracking
    // params (expires_in, token_type, ...) are ignored, never forwarded.
    if (qCreds > 0 || qType || fCreds !== 2) return fail('invalid');
    const access = cleanCredential(fVal('access_token') ?? '', MAX_JWT_CHARS);
    const refresh = cleanCredential(fVal('refresh_token') ?? '', MAX_TOKEN_CHARS);
    if (!access || !refresh || types.length !== 1 || types[0] !== 'recovery') {
      return fragment.length > 0 ? fail('unsupported') : fail('invalid');
    }
    return {
      status: 'ready',
      kind: 'implicit',
      typePresent: true,
      credentials: { accessToken: access, refreshToken: refresh },
    };
  }

  // token + code must not arrive with a fragment.
  if (fragment.length > 0) return fail('invalid');

  const token = qVal('token');
  const code = qVal('code');
  // Exactly one credential key may appear in the query.
  if (qCreds !== 1) return fail('invalid');

  if (token !== undefined) {
    if (types.length !== 1 || types[0] !== 'recovery') return fail('invalid');
    const clean = cleanCredential(token, MAX_TOKEN_CHARS);
    if (!clean) return fail('invalid');
    return { status: 'ready', kind: 'tokenHash', typePresent: true, credentials: { token: clean } };
  }

  if (code !== undefined) {
    const clean = cleanCredential(code, MAX_TOKEN_CHARS);
    if (!clean) return fail('invalid');
    return { status: 'ready', kind: 'pkce', typePresent: types.length === 1, credentials: { code: clean } };
  }

  if (types.length > 0) return fail('invalid');
  return fail('unsupported');
}

/**
 * Bare scheme launch for the post-success "Open RidePulse" action — opens the
 * installed app to its normal state so the user can sign in. Carries no
 * credentials; on Android the manifest only claims `auth/reset-password`, so
 * an unhandled result is expected and the caller shows a fallback.
 */
export const nativeAppLaunchUrl = (platform: Platform): string =>
  `${NATIVE_RECOVERY_SCHEMES[platform]}://`;

const enc = encodeURIComponent;

export function resolveNativeRecoveryContinuation(
  location: RecoveryLocationLike,
  platform: Platform = 'ios'
): NativeRecoveryContinuation {
  const parsed = parseRecoveryCallback(location);
  if (parsed.status !== 'ready') return { status: parsed.status };
  const c = parsed.credentials;
  const target = `${NATIVE_RECOVERY_SCHEMES[platform]}://auth/reset-password`;
  if (parsed.kind === 'tokenHash') {
    return {
      status: 'ready',
      kind: 'tokenHash',
      url: `${target}?token=${enc(c.token!)}&type=recovery`,
    };
  }
  if (parsed.kind === 'pkce') {
    return {
      status: 'ready',
      kind: 'pkce',
      url: `${target}?code=${enc(c.code!)}${parsed.typePresent ? '&type=recovery' : ''}`,
    };
  }
  return {
    status: 'ready',
    kind: 'implicit',
    url: `${target}#access_token=${enc(c.accessToken!)}&refresh_token=${enc(c.refreshToken!)}&type=recovery`,
  };
}
